import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    X, Heart, MessageCircle, Repeat2, BarChart3, Share2, MoreHorizontal, Image, Loader2, AlertCircle, CheckCircle, Save, RotateCcw, FileText
} from 'lucide-react';
import vaultService from '../../../services/vaultService';
import {
    TWITTER_TEMPLATES,
    TWITTER_TONE_OPTIONS,
    TWITTER_CTA_OPTIONS,
    TWITTER_QUICK_ACTIONS,
    OPTIMAL_LENGTHS
} from '../../../ai/prompts/contentPrompts';

const TWITTER_CHAR_LIMIT = OPTIMAL_LENGTHS.twitter.absoluteMax;
const TONE_OPTIONS = TWITTER_TONE_OPTIONS;
const CTA_OPTIONS = TWITTER_CTA_OPTIONS;
const QUICK_ACTIONS = TWITTER_QUICK_ACTIONS;
const buildTwitterPrompt = (params) => params.basePrompt || '';
const parseTwitterPost = (content) => ({ text: content, media: null });
const formatTwitterPost = (post) => post.text || post;
const validateTwitterPost = (content) => ({ isValid: true, errors: [], warnings: [] });
const splitIntoThread = (content) => [content];

/**
 * TwitterPreview - Dynamic Twitter/X post preview component
 * 
 * Displays a Twitter-style post preview with support for:
 * - Dynamic content injection
 * - Streaming content display (typing effect)
 * - Hashtag and mention highlighting
 * - Character count display (280 limit)
 * - Thread detection and indicator
 * - Inline content editing
 * - Media attachment upload via Vault
 * - Drag-and-drop image upload
 * - Unsaved changes tracking
 * - Save to Vault functionality
 * - Start Over functionality
 * 
 * @param {object} props
 * @param {string} props.content - The post content to display
 * @param {boolean} props.isStreaming - Whether content is being streamed
 * @param {string} props.guestName - Guest name for episode display
 * @param {boolean} props.isEditable - Whether content can be edited inline
 * @param {function} props.onContentChange - Callback when content changes
 * @param {boolean} props.hasUnsavedChanges - Whether there are unsaved changes
 * @param {function} props.onSave - Callback to save changes
 * @param {object} props.media - Current media attachment { url, name, type, size, id }
 * @param {function} props.onMediaChange - Callback when media changes (file or null)
 * @param {boolean} props.isUploading - Whether media is being uploaded
 * @param {object} props.episode - Episode data for upload context
 * @param {function} props.onSaveToVault - Callback to save post to vault
 * @param {function} props.onStartOver - Callback to reset and start over
 * @param {boolean} props.isSavingToVault - Whether save to vault is in progress
 */
const TwitterPreview = ({
    content = null,
    isStreaming = false,
    guestName = null,
    isEditable = false,
    onContentChange = null,
    hasUnsavedChanges = false,
    onSave = null,
    media = null,
    onMediaChange = null,
    isUploading = false,
    episode = null,
    onSaveToVault = null,
    onStartOver = null,
    isSavingToVault = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [isDraggingImage, setIsDraggingImage] = useState(false);
    const [isDraggingText, setIsDraggingText] = useState(false);
    const [isInternalUploading, setIsInternalUploading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    // Drag counters to prevent flashing from child element events
    const imageDragCounterRef = useRef(0);
    const textDragCounterRef = useRef(0);

    // Default placeholder content
    const defaultContent = `Just dropped a new episode! 

@amazingguest joins us to discuss building successful startups and the future of AI in podcasting.

A thread on key takeaways 

#Podcasting #AI #StartupGrowth`;

    const displayContent = content || defaultContent;

    // Calculate character count
    const charCount = useMemo(() => {
        return displayContent.length;
    }, [displayContent]);

    // Check if content exceeds limit
    const exceedsLimit = charCount > TWITTER_CHAR_LIMIT;

    // Detect if content is a thread (multiple tweets)
    const isThread = useMemo(() => {
        // Simple thread detection - if content has thread indicators
        return displayContent.includes('thread') ||
            displayContent.includes('Thread') ||
            displayContent.includes('1/') ||
            displayContent.includes('1/)');
    }, [displayContent]);

    // Calculate how many tweets this would be
    const tweetCount = useMemo(() => {
        return Math.ceil(charCount / TWITTER_CHAR_LIMIT) || 1;
    }, [charCount]);

    // Format content with hashtag and mention highlighting
    const formatContent = (text) => {
        if (!text) return null;

        // Split by hashtags and mentions while keeping them
        const parts = text.split(/(#[\w]+|@[\w]+)/g);

        return parts.map((part, index) => {
            if (part.startsWith('#')) {
                return (
                    <span key={index} className="text-blue-500 hover:underline cursor-pointer">
                        {part}
                    </span>
                );
            }
            if (part.startsWith('@')) {
                return (
                    <span key={index} className="text-blue-500 hover:underline cursor-pointer">
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    // Handle text change in editable mode
    const handleTextChange = (e) => {
        if (onContentChange) {
            onContentChange(e.target.value);
        }
    };

    // Handle click to start editing
    const handleContentClick = () => {
        if (isEditable && !isStreaming) {
            setIsEditing(true);
        }
    };

    // Handle blur to stop editing
    const handleBlur = () => {
        setIsEditing(false);
    };

    // Auto-focus textarea when editing starts
    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isEditing]);

    // Handle file selection for image upload
    const handleFileSelect = async (file) => {
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setUploadError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
            return;
        }

        // Validate file size (max 5MB for Twitter)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setUploadError('Image size must be less than 5MB');
            return;
        }

        setUploadError(null);

        // If parent provides onMediaChange, use it (parent handles upload)
        if (onMediaChange) {
            await onMediaChange(file);
        } else {
            // Handle upload internally
            setIsInternalUploading(true);
            try {
                const result = await vaultService.uploadFile(file, {
                    episodeId: episode?.id,
                    tags: ['twitter', 'social-media', 'content-creation'],
                    description: `Twitter post image for ${episode?.title || 'content'}`
                });

                const asset = result.asset || result;

                // Set the uploaded media with its URL
                onMediaChange({
                    url: asset.downloadUrl || asset.url || asset.fileUrl,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    id: asset.id || asset.assetId
                });
            } catch (error) {
                console.error('Failed to upload media:', error);
                setUploadError('Failed to upload image. Please try again.');
            } finally {
                setIsInternalUploading(false);
            }
        }
    };

    // Handle input change event
    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
        // Reset input value to allow re-selecting the same file
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle drag events for IMAGE zone - using counter pattern to prevent flashing
    const handleImageDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();

        imageDragCounterRef.current++;
        if (imageDragCounterRef.current === 1) {
            // Check if dragging an image asset
            if (e.dataTransfer.types.includes('application/json')) {
                setIsDraggingImage(true);
            } else if (e.dataTransfer.types.includes('Files')) {
                setIsDraggingImage(true);
            }
        }
    };

    const handleImageDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        imageDragCounterRef.current--;
        if (imageDragCounterRef.current === 0) {
            setIsDraggingImage(false);
        }
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleImageDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Reset counter
        imageDragCounterRef.current = 0;
        setIsDraggingImage(false);

        // Check for vault asset drag (from VaultDrawer)
        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);

                // Only handle IMAGE assets from vault
                if (artifact && artifact.type === 'vault-asset' && artifact.assetType === 'image') {
                    onMediaChange({
                        url: artifact.downloadUrl,
                        name: artifact.fileName || artifact.title,
                        type: artifact.mimeType || 'image/jpeg',
                        size: artifact.size,
                        id: artifact.id
                    });
                    return;
                } else if (artifact && artifact.type === 'vault-asset' && artifact.assetType !== 'image') {
                    // Not an image - show error
                    setUploadError('Only images can be dropped here. Text should be dropped on the text area.');
                    setTimeout(() => setUploadError(null), 3000);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse vault asset:', err);
            }
        }

        // Handle file drops from OS - validate it's an image
        const file = e.dataTransfer.files?.[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                setUploadError('Only image files can be dropped here. Text should be dropped on the text area.');
                setTimeout(() => setUploadError(null), 3000);
                return;
            }
            handleFileSelect(file);
        }
    };

    // Handle drag events for TEXT zone - using counter pattern to prevent flashing
    const handleTextDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();

        textDragCounterRef.current++;
        if (textDragCounterRef.current === 1) {
            // Check if dragging a text asset
            if (e.dataTransfer.types.includes('application/json')) {
                setIsDraggingText(true);
            } else if (e.dataTransfer.types.includes('text/plain')) {
                setIsDraggingText(true);
            }
        }
    };

    const handleTextDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        textDragCounterRef.current--;
        if (textDragCounterRef.current === 0) {
            setIsDraggingText(false);
        }
    };

    const handleTextDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleTextDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Reset counter
        textDragCounterRef.current = 0;
        setIsDraggingText(false);

        // Check for vault asset drag (from VaultDrawer)
        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);

                // Only handle TEXT assets from vault
                if (artifact && artifact.type === 'vault-asset' && artifact.assetType === 'text' && artifact.content) {
                    if (onContentChange) {
                        onContentChange(artifact.content);
                    }
                    return;
                } else if (artifact && artifact.type === 'vault-asset' && artifact.assetType !== 'text') {
                    // Not text - show error
                    setUploadError('Only text content can be dropped here. Images should be dropped on the image area.');
                    setTimeout(() => setUploadError(null), 3000);
                    return;
                }
            } catch (err) {
                console.error('Failed to parse vault asset:', err);
            }
        }

        // Handle plain text drops
        if (e.dataTransfer.types.includes('text/plain')) {
            const text = e.dataTransfer.getData('text/plain');
            if (text && onContentChange) {
                onContentChange(text);
            }
        }
    };

    // Handle remove image
    const handleRemoveImage = () => {
        if (onMediaChange) {
            onMediaChange(null);
        }
        setUploadError(null);
    };

    // Trigger file input click
    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    // Determine if upload is in progress (either internal or from parent)
    const showUploading = isUploading || isInternalUploading;

    return (
        <div className="flex flex-col h-full font-sans items-center pt-4">
            {/* Action Buttons - Save to Vault & Start Over */}
            {isEditable && content && (
                <div className="w-full max-w-[550px] mb-2 px-2">
                    <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-3 py-2">
                        <div className="flex items-center gap-3">
                            {/* Character Count */}
                            <div className={`flex items-center gap-1 text-xs font-medium ${exceedsLimit ? 'text-red-500' : charCount > 250 ? 'text-amber-500' : 'text-gray-500'}`}>
                                <span>{charCount}</span>
                                <span>/</span>
                                <span>{TWITTER_CHAR_LIMIT}</span>
                                {isThread && !exceedsLimit && (
                                    <span className="ml-2 text-blue-500">({tweetCount} tweets)</span>
                                )}
                            </div>
                            <div className="w-px h-4 bg-gray-200" />
                            {saveSuccess ? (
                                <>
                                    <CheckCircle size={14} className="text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">Saved to Vault!</span>
                                </>
                            ) : hasUnsavedChanges ? (
                                <>
                                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                    <span className="text-xs text-amber-600 font-medium">Unsaved</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={14} className="text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">Ready to save</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onStartOver}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all"
                            >
                                <RotateCcw size={14} />
                                Start Over
                            </button>
                            <button
                                onClick={async () => {
                                    if (onSaveToVault) {
                                        const success = await onSaveToVault();
                                        if (success) {
                                            setSaveSuccess(true);
                                            setTimeout(() => setSaveSuccess(false), 2000);
                                        }
                                    }
                                }}
                                disabled={isSavingToVault || !content || exceedsLimit}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isSavingToVault
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : exceedsLimit
                                        ? 'bg-red-100 text-red-400 cursor-not-allowed'
                                        : 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'
                                    }`}
                            >
                                {isSavingToVault ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={14} />
                                        Save to Vault
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Feed Content - Scrollable */}
            <div className="w-full max-w-full overflow-y-auto scrollbar-hide px-0">
                {/* Post Card */}
                <div className="bg-white rounded-none sm:rounded-xl border border-gray-100 hover:bg-gray-50/50 transition-colors cursor-pointer border-b border-gray-200 mx-auto max-w-[550px]">
                    <div className="flex p-4 gap-3">
                        {/* Avatar Column */}
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-900 to-black flex items-center justify-center hover:opacity-90 cursor-pointer">
                                <span className="text-white font-bold text-xs">PG</span>
                            </div>
                        </div>

                        {/* Content Column */}
                        <div className="flex-1 min-w-0">
                            {/* Header Row */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1 text-[15px] leading-5 truncate">
                                    <span className="font-bold text-gray-900 hover:underline cursor-pointer">PodGuestly</span>
                                    {/* Verified Checkmark */}
                                    <svg viewBox="0 0 24 24" aria-label="Verified account" role="img" className="w-[18px] h-[18px] text-blue-500 fill-current">
                                        <g>
                                            <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .495.083.965.238 1.4-1.272.65-2.147 2.02-2.147 3.6 0 1.435.716 2.69 1.77 3.46-.254.58-.415 1.205-.415 1.845 0 2.455 1.99 4.45 4.45 4.45.64 0 1.265-.16 1.845-.415.77 1.054 2.025 1.77 3.46 1.77s2.69-.716 3.46-1.77c.58.254 1.205.415 1.845.415 2.46 0 4.45-1.995 4.45-4.45 0-.64-.16-1.265-.415-1.845 1.054-.77 1.77-2.025 1.77-3.46zM12 17.5c-3.037 0-5.5-2.46-5.5-5.5S8.963 6.5 12 6.5s5.5 2.463 5.5 5.5-2.463 5.5-5.5 5.5zm1.5-8.5l-3 3-1.5-1.5-1.5 1.5 3 3 4.5-4.5-1.5-1.5z"></path>
                                        </g>
                                    </svg>
                                    <span className="text-gray-500 font-normal ml-0.5">@podguestly</span>
                                    <span className="text-gray-500 font-normal px-1">.</span>
                                    <span className="text-gray-500 font-normal hover:underline cursor-pointer">2h</span>
                                </div>
                                <div className="group relative">
                                    <div className="p-2 -m-2 rounded-full group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors cursor-pointer">
                                        <MoreHorizontal size={18} className="text-gray-500 group-hover:text-blue-500" />
                                    </div>
                                </div>
                            </div>

                            {/* Tweet Text - Click to Edit or Display Mode (TEXT DROP ZONE) */}
                            <div
                                className="mt-0.5 text-[15px] leading-snug text-gray-900 whitespace-pre-wrap"
                                onDragEnter={handleTextDragEnter}
                                onDragLeave={handleTextDragLeave}
                                onDragOver={handleTextDragOver}
                                onDrop={handleTextDrop}
                            >
                                {isEditing && isEditable && !isStreaming ? (
                                    <textarea
                                        ref={textareaRef}
                                        value={displayContent}
                                        onChange={handleTextChange}
                                        onBlur={handleBlur}
                                        className={`w-full text-[15px] leading-snug text-gray-900 whitespace-pre-wrap resize-none border transition-all outline-none bg-white rounded-lg p-2 -mx-2 -my-2 ${isDraggingText ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-blue-300 focus:ring-2 focus:ring-blue-100'}`}
                                        placeholder="What's happening?"
                                        rows={displayContent.split('\n').length + 2}
                                    />
                                ) : (
                                    <div
                                        onClick={handleContentClick}
                                        className={`cursor-default transition-all rounded-lg ${isDraggingText ? 'bg-blue-50 border-2 border-dashed border-blue-400 p-8 min-h-[120px] flex items-center justify-center' : 'p-2 -mx-2 -my-2'} ${isEditable && !isStreaming ? 'cursor-text hover:bg-gray-50' : ''}`}
                                    >
                                        {isDraggingText ? (
                                            <div className="text-center">
                                                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-blue-100 flex items-center justify-center">
                                                    <FileText size={24} className="text-blue-600" />
                                                </div>
                                                <span className="text-blue-600 font-semibold text-base">
                                                    Drop text content here
                                                </span>
                                                <p className="text-blue-400 text-xs mt-1">
                                                    Release to insert text
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="whitespace-pre-wrap leading-snug">
                                                {formatContent(displayContent)}
                                                {isStreaming && (
                                                    <span className="inline-block w-1.5 h-4 bg-blue-500 animate-pulse ml-0.5" />
                                                )}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Media Content - Interactive Upload Zone (IMAGE DROP ZONE) */}
                            <div
                                className={`mt-3 rounded-2xl border border-gray-200 overflow-hidden ${!media && !showUploading ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}`}
                                onClick={!media && !showUploading ? handleUploadClick : undefined}
                                onDragEnter={handleImageDragEnter}
                                onDragLeave={handleImageDragLeave}
                                onDragOver={handleImageDragOver}
                                onDrop={handleImageDrop}
                            >
                                {/* Hidden file input */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    onChange={handleInputChange}
                                    className="hidden"
                                />

                                {showUploading ? (
                                    /* Loading State */
                                    <div className="aspect-[1.91/1] bg-gray-100 relative">
                                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-tr from-gray-100 to-gray-200">
                                            <div className="text-center">
                                                <div className="mx-auto w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-2">
                                                    <Loader2 size={20} className="text-blue-500 animate-spin" />
                                                </div>
                                                <p className="text-sm text-gray-500">Uploading...</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : media ? (
                                    /* Uploaded Image Preview */
                                    <div className="aspect-[1.91/1] bg-gray-100 relative group">
                                        <img
                                            src={media.url}
                                            alt="Uploaded media"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                console.error('Image failed to load:', media.url);
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        {/* Fallback if image fails to load */}
                                        <div
                                            className="absolute inset-0 bg-gray-200 flex items-center justify-center"
                                            style={{ display: 'none' }}
                                        >
                                            <div className="text-center">
                                                <AlertCircle size={24} className="text-gray-400 mx-auto mb-2" />
                                                <p className="text-sm text-gray-500">Failed to load image</p>
                                            </div>
                                        </div>
                                        {/* Remove Image Button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveImage();
                                            }}
                                            className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-colors opacity-0 group-hover:opacity-100"
                                            title="Remove image"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    /* Upload Placeholder */
                                    <div className={`aspect-[1.91/1] bg-gray-100 relative transition-colors ${isDraggingImage ? 'bg-blue-50' : ''}`}>
                                        <div className={`absolute inset-0 flex items-center justify-center ${isDraggingImage ? 'bg-blue-50' : 'bg-gradient-to-tr from-gray-100 to-gray-200'}`}>
                                            <div className="text-center">
                                                <div className="mx-auto w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-2">
                                                    <Image size={20} className={`${isDraggingImage ? 'text-blue-500' : 'text-gray-400'}`} />
                                                </div>
                                                <p className="text-sm text-gray-500">
                                                    {isDraggingImage ? 'Drop image here' : (guestName ? `Episode with ${guestName}` : 'Add image')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Upload Error Message */}
                            {uploadError && (
                                <div className="mt-2">
                                    <div className="flex items-center gap-2 text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">
                                        <AlertCircle size={14} />
                                        <span>{uploadError}</span>
                                    </div>
                                </div>
                            )}

                            {/* Thread Indicator */}
                            {isThread && !exceedsLimit && (
                                <div className="mt-2 flex items-center gap-2 text-xs text-blue-500">
                                    <span className="font-medium">Show this thread</span>
                                    <span className="text-gray-400">({tweetCount} tweets)</span>
                                </div>
                            )}

                            {/* Engagement Action Bar */}
                            <div className="mt-3 flex items-center justify-between max-w-[425px]">
                                {/* Reply */}
                                <div className="flex items-center group cursor-pointer text-gray-500 hover:text-blue-500">
                                    <div className="p-2 -ml-2 rounded-full group-hover:bg-blue-50 transition-colors">
                                        <MessageCircle size={18} className="stroke-2" />
                                    </div>
                                    <span className="text-[13px] px-1 group-hover:text-blue-500">12</span>
                                </div>

                                {/* Repost */}
                                <div className="flex items-center group cursor-pointer text-gray-500 hover:text-green-500">
                                    <div className="p-2 -ml-2 rounded-full group-hover:bg-green-50 transition-colors">
                                        <Repeat2 size={18} className="stroke-2" />
                                    </div>
                                    <span className="text-[13px] px-1 group-hover:text-green-500">48</span>
                                </div>

                                {/* Like */}
                                <div className="flex items-center group cursor-pointer text-gray-500 hover:text-pink-500">
                                    <div className="p-2 -ml-2 rounded-full group-hover:bg-pink-50 transition-colors">
                                        <Heart size={18} className="stroke-2" />
                                    </div>
                                    <span className="text-[13px] px-1 group-hover:text-pink-500">247</span>
                                </div>

                                {/* View/Analytics */}
                                <div className="flex items-center group cursor-pointer text-gray-500 hover:text-blue-500">
                                    <div className="p-2 -ml-2 rounded-full group-hover:bg-blue-50 transition-colors">
                                        <BarChart3 size={18} className="stroke-2" />
                                    </div>
                                    <span className="text-[13px] px-1 group-hover:text-blue-500">1.2K</span>
                                </div>

                                {/* Share */}
                                <div className="flex items-center group cursor-pointer text-gray-500 hover:text-blue-500">
                                    <div className="p-2 -ml-2 rounded-full group-hover:bg-blue-50 transition-colors">
                                        <Share2 size={18} className="stroke-2" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Re-export constants (now defined locally above)
export { TWITTER_TEMPLATES, buildTwitterPrompt };

export default TwitterPreview;
